/**
 * 表单提交 Hook
 *
 * 封装表单提交的常见逻辑：loading 状态管理、防重复提交、成功/失败回调。
 * 适用于各种表单场景（ProForm、Modal 表单、独立表单页等）。
 *
 * 通用表单提交处理
 */

import { useCallback, useRef, useState } from 'react';
import type { ApiResponse } from '../types/api';
import { isApiSuccess } from '../utils/api-helpers';
import { useLatestRef } from './useLatestRef';

/**
 * useFormSubmit 配置
 */
export interface UseFormSubmitOptions<
  T = Record<string, unknown>,
  R = unknown,
> {
  /** 提交 API 函数 */
  submitApi: (data: T) => Promise<ApiResponse<R>>;
  /** 成功状态码，默认 '200' */
  successCode?: string | number;
  /** 提交前的数据转换 */
  transformData?: (data: T) => T;
  /** 成功提示消息 */
  successMsg?: string;
  /** 消息展示函数 */
  showMessage?: (content: string, type: 'success' | 'error') => void;
  /** 提交成功回调 */
  onSuccess?: (data: R) => void;
  /** 提交失败回调 */
  onError?: (error: Error) => void;
  /** 提交完成回调（无论成功或失败） */
  onFinally?: () => void;
}

/**
 * useFormSubmit 返回值
 */
export interface UseFormSubmitReturn<T = Record<string, unknown>> {
  /** 提交中状态 */
  submitting: boolean;
  /** 提交函数 */
  submit: (data: T) => Promise<boolean>;
  /** 重置提交状态 */
  reset: () => void;
}

/**
 * 表单提交 Hook
 *
 * @example
 * ```tsx
 * // 基础用法
 * const { submitting, submit } = useFormSubmit({
 *   submitApi: createUser,
 *   successMsg: '创建成功',
 *   showMessage: (content, type) => message[type](content),
 *   onSuccess: () => history.push('/user/list'),
 * });
 *
 * <ProForm loading={submitting} onFinish={submit} />
 *
 * // Modal 表单
 * const { submitting, submit } = useFormSubmit({
 *   submitApi: (data) => updateUser(id, data),
 *   successMsg: '更新成功',
 *   showMessage: (content, type) => message[type](content),
 *   onSuccess: () => { modal.close(); actionRef.current?.reload(); },
 * });
 *
 * <ModalForm loading={submitting} onFinish={submit} />
 * ```
 */
export function useFormSubmit<T = Record<string, unknown>, R = unknown>(
  options: UseFormSubmitOptions<T, R>,
): UseFormSubmitReturn<T> {
  const { submitApi, successCode = '200', successMsg } = options;

  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  // 用 useLatestRef 稳定所有可能不稳定的回调
  const transformDataRef = useLatestRef(options.transformData);
  const onSuccessRef = useLatestRef(options.onSuccess);
  const onErrorRef = useLatestRef(options.onError);
  const showMessageRef = useLatestRef(options.showMessage);
  const onFinallyRef = useLatestRef(options.onFinally);

  const submit = useCallback(
    async (data: T): Promise<boolean> => {
      // 防重复提交
      if (submittingRef.current) return false;

      submittingRef.current = true;
      setSubmitting(true);

      try {
        const submitData = transformDataRef.current
          ? transformDataRef.current(data)
          : data;

        const response = await submitApi(submitData);
        const isSuccess = isApiSuccess(response, successCode);

        if (isSuccess) {
          if (successMsg) {
            showMessageRef.current?.(successMsg, 'success');
          }
          onSuccessRef.current?.(response.data);
          return true;
        }

        const errorMessage =
          response.msg ||
          response.message ||
          response.errorMessage ||
          'Operation failed';
        showMessageRef.current?.(errorMessage, 'error');
        return false;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        showMessageRef.current?.(err.message || 'Operation failed', 'error');
        onErrorRef.current?.(err);
        return false;
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
        onFinallyRef.current?.();
      }
    },
    [
      submitApi,
      successCode,
      successMsg,
      onErrorRef,
      onFinallyRef,
      onSuccessRef,
      showMessageRef,
      transformDataRef,
    ],
  );

  const reset = useCallback(() => {
    submittingRef.current = false;
    setSubmitting(false);
  }, []);

  return { submitting, submit, reset };
}
